import { useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import api from './lib/api';
import { teamAPI, setEntityLeagueScope } from './lib/api';
import { posthogCapture } from '@/posthog.config';
import { adTrackPurchase } from './lib/adPixels';
import { toast } from 'sonner';
import { AdProvider, useAds } from './contexts/AdContext';
import AdPopup from './components/AdPopup';
import AdPixelsTracker from './components/AdPixelsTracker';
import InstallAppBanner from './components/InstallAppBanner';
import GuestFirstDraftPrompt from './components/GuestFirstDraftPrompt';
import FinalizeNowPrompt from './components/FinalizeNowPrompt';
import QuarterRecapPrompt from './components/QuarterRecapPrompt';
import { Toaster } from './components/ui/sonner';
import SplashScreen from './pages/SplashScreen';
import CookieBanner from './components/CookieBanner';
import AuthPage from './pages/AuthPage';
import GlobalHeader from './components/layout/GlobalHeader';
import BottomTabBar from './components/layout/BottomTabBar';
import ErrorBoundary from './components/ErrorBoundary';
import StageTab from './pages/StageTab';
import NewsTab from './pages/NewsTab';
import RankingsTab from './pages/RankingsTab';
import ExploreTab from './pages/ExploreTab';
import MoreTab from './pages/MoreTab';
// M-3 (2026-01) code-splitting: admin/audit/brand pages are large and
// rarely loaded by regular users (AdminDashboard ~2.3k LOC, AuditDashboard
// ~1.6k LOC). Lazy-loading them keeps the main bundle small and stops
// shipping admin UI to every visitor. Loaded on demand behind <Suspense>.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminPortraitsPage = lazy(() => import('./pages/AdminPortraitsPage'));
import { Navigate } from 'react-router-dom';
const AuditDashboard = lazy(() => import('./pages/AuditDashboard'));  // v6.1.15 Phase K.2
const AdminChatModerationPage = lazy(() => import('./pages/AdminChatModerationPage'));
import EntityDetail from './pages/EntityDetail';
import DraftRoom from './pages/DraftRoom';
import TeamManagement from './pages/TeamManagement';
import NotificationsPanel from './pages/NotificationsPanel';
import CommunityChat from './pages/CommunityChat';
import PremiumPage from './pages/PremiumPage';
import BoostersPage from './pages/BoostersPage';
import TransferMarket from './pages/TransferMarket';
import ScoringRulesViewer from './pages/ScoringRulesViewer';
import ProfilePage from './pages/ProfilePage';
import WatchlistPage from './pages/WatchlistPage';
import LeagueActivityPage from './pages/LeagueActivityPage';
import UserStatsPage from './pages/UserStatsPage';
import FormationView from './pages/FormationView';
import AboutPage from './pages/AboutPage';
import TeamViewPage from './pages/TeamViewPage';
import HowToPlayAndRules from './pages/HowToPlayAndRules';
import HowToPlayOnboarding from './pages/HowToPlayOnboarding';
import LeagueEntitySelector from './pages/LeagueEntitySelector';
import PeopleBrowsePage from './pages/PeopleBrowsePage';
import LegalTakedownPage from './pages/LegalTakedownPage';
import LegalInclusionPage from './pages/LegalInclusionPage';
import CreditsPage from './pages/CreditsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OnboardingBanner from './components/OnboardingBanner';
import OnboardingAttachBanner from './components/OnboardingAttachBanner';
import { useOnboardingAttachStatus } from './hooks/useOnboardingAttachStatus';
import DeveloperNewsScreen from './pages/DeveloperNewsScreen';
import { Skeleton } from './components/ui/skeleton';
import PreLaunchGate from './components/PreLaunchGate';
// v6.1.17.7 N.2 — global username-picker mount
import UsernamePickerHost from './components/UsernamePickerModal';
// 18+ gate (2026-06) — global age-confirmation recovery mount
import { AgeGateHost } from './components/AgeGateModal';
// v6.1.17.7 N.2-OAuth — OAuth callback
import OAuthCallback from './pages/OAuthCallback';

function MainApp() {
  const { user, loading, isFirstLogin, clearFirstLogin, refreshUser } = useAuth();
  const { triggerAdCheck } = useAds();
  // PERF / TESTING (v5.7.37): ?nosplash=1 (or sessionStorage flag) skips the
  // splash so end-to-end perf benchmarks + automated tests don't have to wait
  // ~5 seconds for the globe animation. Production users never see this query
  // param. Harmless to leave on permanently for power users too.
  //
  // v6.1.4 P0 — Session-active flag (`fws_session_active`) ALSO skips the
  // splash and DevNews modal on the SAME tab.  Pre-fix: a returning user
  // who reloaded a page they were already authenticated on saw the full
  // splash → DevNews → dashboard re-play.  Tester reported this as "bounced
  // back to landing/login" — technically the session WAS preserved (the
  // cookie + /auth/refresh + /auth/me path all worked) but the UX made it
  // feel like a bounce.  Flag is stored in sessionStorage so:
  //   - reload within the same tab → splash skipped (no re-play)
  //   - close tab + reopen → flag gone → splash plays (matches "fresh
  //     launch" mental model)
  //   - logout → flag cleared explicitly so the next login sees splash
  //
  // 2026-07 audit P1 — the old module-body IIFEs re-read sessionStorage on
  // EVERY MainApp render. Lazy useState initializer runs exactly once per
  // mount. (The old `skipDevNews` IIFE was dead code — handleSplashComplete
  // re-reads `fws_session_active` itself.)
  const [showSplash, setShowSplash] = useState(() => {
    try {
      if (typeof window === 'undefined') return true;
      if (new URLSearchParams(window.location.search).get('nosplash') === '1') return false;
      if (sessionStorage.getItem('fws_skip_splash') === '1') return false;
      if (sessionStorage.getItem('fws_session_active') === '1') return false;
      return true;
    } catch { return true; }
  });
  const [showDevNews, setShowDevNews] = useState(false);
  const [showFirstTimeGuide, setShowFirstTimeGuide] = useState(false);
  const [activeTab, setActiveTab] = useState('stage');
  // v5.7.81 — Navigation stack (replaces the single-slot subView/subViewData).
  //
  // Why a stack
  // ===========
  // Today's single-slot model loses history: Stage → team-view → entity-detail
  // and back-arrow snaps to Stage, NOT to team-view. Users expect one back
  // per push. The fix is a per-mount stack: navigateTo pushes, goBack pops one.
  //
  // Why ALL entries stay mounted (lower ones hidden via display:none)
  // ================================================================
  // Sub-views often have scroll state, search filters, draft selections, etc.
  // The v5.7.60 fixed-overlay trick already preserves the TAB ROOT's scroll
  // for free; this extends the same trick down the stack so EVERY level
  // keeps its state. Cost: stack-depth × DOM nodes — but stack depth caps
  // at ~4 in practice, so memory is negligible.
  //
  // popstate wiring
  // ===============
  // Each navigateTo calls pushUrl which adds a `history.pushState` entry,
  // so the browser/device back gesture fires popstate. The URL→state
  // effect detects path changes and falls through to whatever route the
  // new URL maps to. When the path is a tab-root path (e.g. /, /news),
  // we drain the entire stack (back-from-deep-link goes home).
  const [navStack, setNavStack] = useState([]);   // [{view, data}]
  // Legacy aliases — derived from the top of the stack so existing
  // call-sites (subViewRef, etc.) keep working without churn.
  const subView = navStack.length ? navStack[navStack.length - 1].view : null;
  const subViewData = navStack.length ? navStack[navStack.length - 1].data : null;
  
  // Phase D — onboarding-attach progress (auto-no-op for existing users).
  // Only poll when the user is authenticated to avoid pointless 401s.
  const attachStatus = useOnboardingAttachStatus({ enabled: !!user });

  // Master league + team state
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(null);

  // 2026-06 (iter_187 P0) — single recency-guarded writer for currentTeam.
  // A GlobalHeader remount (returning from Draft Room) used to clobber the
  // fresh post-draft-random snapshot with its own pre-draft/null fetch
  // result, leaving Stage on a stale roster. Same-team writes only win when
  // NEWER (updated_at first, filled-slot count as tiebreak); null never
  // replaces an existing team here (league switches go through setCurrentTeam
  // directly, where "no team in that league" is legitimate).
  const adoptTeamSnapshot = useCallback((incoming) => {
    setCurrentTeam((prev) => {
      if (!incoming) return prev;
      if (!prev || prev.id !== incoming.id) return incoming;
      const ts = (t) => new Date(t?.updated_at || 0).getTime();
      if (ts(incoming) !== ts(prev)) return ts(incoming) > ts(prev) ? incoming : prev;
      return (incoming.slots || []).length >= (prev.slots || []).length ? incoming : prev;
    });
  }, []);

  // Live balance refresh: rewarded-ad grants (and any ₣-balance mutation) fire
  // `fws:team-refresh`; re-pull the active-league team so Stage/Net-Worth update
  // without a reload. Draft/TeamManagement re-fetch on mount, so this covers Stage.
  // 2026-07 UX audit P0 — in-flight dedupe: bursts of fws:team-refresh
  // (score floaters, multi-reward grants) previously fired N identical
  // parallel /teams/my calls. One fetch covers the whole burst.
  // 2026-06 (iter_187) — coalesce instead of DROP: an event arriving while a
  // fetch is in flight now re-runs once it resolves (a post-mutation refresh
  // used to be silently lost, another source of the stale-Stage bug).
  const teamRefreshInflightRef = useRef(false);
  const teamRefreshPendingRef = useRef(false);
  useEffect(() => {
    const onRefresh = async () => {
      if (teamRefreshInflightRef.current) { teamRefreshPendingRef.current = true; return; }
      teamRefreshInflightRef.current = true;
      try {
        do {
          teamRefreshPendingRef.current = false;
          try {
            const res = await teamAPI.myTeams();
            const teams = res.data?.teams || [];
            // iter_189 — STRICT league match (no teams[0] fallback): never
            // adopt another league's team under this league's name.
            const pick = teams.find((t) => t.league_id === selectedLeagueId) || null;
            if (pick) adoptTeamSnapshot(pick);
            else setCurrentTeam((prev) => (prev?.league_id === selectedLeagueId ? prev : null));
          } catch (_e) { /* non-blocking */ }
        } while (teamRefreshPendingRef.current);
      } finally { teamRefreshInflightRef.current = false; }
    };
    window.addEventListener('fws:team-refresh', onRefresh);
    return () => window.removeEventListener('fws:team-refresh', onRefresh);
  }, [selectedLeagueId, adoptTeamSnapshot]);

  // 2026-07-23 — keep the api layer's entity league scope in sync so every
  // /entities call shows league-scoped, finalized-only % owned.
  useEffect(() => { setEntityLeagueScope(selectedLeagueId); }, [selectedLeagueId]);

  const location = useLocation();
  const navigate = useNavigate();
  // 2026-06 (iter_187) — returning to a tab root from any sub-view re-pulls
  // the team (coalesced above) so Stage always self-heals from a stale
  // snapshot, whatever happened inside the sub-view.
  const prevSubViewRef = useRef(null);
  useEffect(() => {
    if (prevSubViewRef.current && !subView) {
      try { window.dispatchEvent(new CustomEvent('fws:team-refresh')); } catch (_e) { /* noop */ }
    }
    prevSubViewRef.current = subView;
  }, [subView]);
  const lastHandledPath = useRef(null);
  // 2026-07-20 perf audit — deep-link router reads the team id via ref so a
  // routine team refresh doesn't re-run the URL effect.
  const currentTeamIdRef = useRef(null);
  currentTeamIdRef.current = currentTeam?.id;
  // 2026-06 audit (P1): set to 'popstate' by the back-gesture handler when it
  // unwinds a sub-view, so the URL→state effect skips re-mapping the stack.
  const navSourceRef = useRef(null);
  // 2026-08-12 (owner request) — transient context for the "Bought For" card:
  // set to the OPPONENT team when drilling team-view → person, else null.
  // NOT carried in the URL (id-only), so a deep-link/refresh cleanly shows the
  // viewer's own purchase price instead.
  const entityContextTeamRef = useRef(null);

  // v6.x (Task 0) — Enrich the selected league with its full `settings`
  // (slot_config / allowed_categories / required_per_category). The
  // `GET /api/leagues` LIST response deliberately OMITS `settings` for
  // payload size, but ExploreTab / StageTab / DraftRoom / OnboardingBanner
  // need slot_config to drive per-league draft availability (faded + "N/A"
  // rows). The detail endpoint carries it, so fetch + merge on selection.
  // 2026-07 UX audit P1 — stale-while-revalidate cache: switching leagues
  // back and forth re-fetched the same detail payload every time and left
  // dependent tabs waiting on `settings`. Serve cached instantly, refresh
  // silently in the background.
  const leagueCacheRef = useRef(new Map());
  useEffect(() => {
    if (!selectedLeagueId) return;
    let cancelled = false;
    const cached = leagueCacheRef.current.get(selectedLeagueId);
    if (cached) {
      setSelectedLeague((prev) =>
        prev && prev.id === cached.id ? { ...prev, ...cached } : cached
      );
    }
    (async () => {
      try {
        const res = await api.get(`/leagues/${selectedLeagueId}`);
        const lg = res.data?.league;
        if (lg) {
          leagueCacheRef.current.set(selectedLeagueId, lg);
          if (!cancelled) {
            setSelectedLeague((prev) =>
              prev && prev.id === lg.id ? { ...prev, ...lg } : lg
            );
          }
        }
      } catch (e) {
        // Non-fatal: keep the lightweight list object; consumers fall
        // back to "all categories allowed" (correct for the global league).
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLeagueId]);

  // 2026-08-15 (pool editing) — refresh the cached league detail when the
  // people-pool editor saves, so "custom pool: N" badges update live.
  useEffect(() => {
    const onPoolUpdated = async (e) => {
      const lid = e?.detail?.leagueId;
      if (!lid) return;
      leagueCacheRef.current.delete(lid);
      try {
        const res = await api.get(`/leagues/${lid}`);
        const lg = res.data?.league;
        if (lg) {
          leagueCacheRef.current.set(lid, lg);
          setSelectedLeague((prev) => (prev && prev.id === lid ? { ...prev, ...lg } : prev));
        }
      } catch { /* non-fatal */ }
    };
    window.addEventListener('fws:league-pool-updated', onPoolUpdated);
    return () => window.removeEventListener('fws:league-pool-updated', onPoolUpdated);
  }, []);


  // Reset ALL navigation state whenever user changes (login/logout).
  // Also clear any stale path in the address bar so a fresh login always
  // lands on Stage (not on a `/news` or `/more` URL inherited from the
  // previous session).
  const prevUserRef = useRef(user?.id);
  useEffect(() => {
    if (user?.id !== prevUserRef.current) {
      setActiveTab('stage');
      setNavStack([]);
      window.scrollTo(0, 0);
      // Only normalize tab paths — keep shareable deep links (/team/:id,
      // /person/:id, /league/:id, /draft/:id) so the URL→state effect can
      // route them properly on the next tick. 2026-06 (Task 1) — ALSO keep
      // the tab-root paths (/stage,/explore,/news,/rankings,/more): a hard
      // reload or a return from /legal/takedown (which unmounts MainApp) must
      // land back on the SAME tab, not bounce to Stage. The URL→state effect
      // restores the matching activeTab. Only truly stale/unknown paths reset.
      const path = window.location.pathname || '/';
      const isShareableDeepLink = /^\/(person|team|league|draft)\//.test(path);
      const isTabRoot = /^\/(stage|explore|news|rankings|more)$/.test(path);
      if (!isShareableDeepLink && !isTabRoot && path !== '/') {
        lastHandledPath.current = '/';
        navigate('/', { replace: true });
      }
      prevUserRef.current = user?.id;
    }
  }, [user?.id, navigate]);

  // ─── Helper: push a URL to history WITHOUT re-triggering the URL→state effect.
  // We set `lastHandledPath` to the target path first so the effect sees a
  // matching value and short-circuits. Safe even if the path equals the
  // current one.
  const pushUrl = useCallback((path) => {
    if (!path) return;
    if (location.pathname === path) return;        // no-op
    lastHandledPath.current = path;                // pre-arm the dedupe
    navigate(path, { replace: false });            // back/forward works
  }, [navigate, location.pathname]);

  // ─── Deep-link URL → tab / sub-view router ────────────────────────────
  // Reads the path on mount and whenever it changes (e.g. someone opens
  //   https://your-domain.com/team/abc123
  // from a Slack DM in their desktop browser). Maps to the same internal
  // state the in-app navigation uses, so the URL and the UI stay in sync
  // with the mobile deep-linking config from v5.7.10.
  useEffect(() => {
    if (!user || loading) return;                           // wait for auth
    if (lastHandledPath.current === location.pathname) return; // idempotent
    lastHandledPath.current = location.pathname;

    // 2026-06 audit (P1, 3/3 consensus) — if this path change came from a
    // browser/device BACK gesture that unwound a sub-view, the popstate
    // handler below already popped exactly one stack frame. Do NOT also
    // re-map the URL into a fresh single-entry stack here — that conflict
    // collapsed a multi-level stack straight to root. Let popstate own it.
    if (navSourceRef.current === 'popstate') {
      navSourceRef.current = null;
      window.scrollTo(0, 0);
      return;
    }

    const path = location.pathname || '/';

    // v5.7.61 — Stripe Checkout success / cancel redirect handling.
    // /premium/success?session_id=cs_…  → poll /billing/session-status
    //                                    until premium flag flips, then
    //                                    toast + scrub the URL.
    // /premium/cancel                  → toast + scrub.
    if (path === '/premium/success') {
      const sid = new URLSearchParams(window.location.search).get('session_id');
      (async () => {
        // 2026-07 UX audit P0 — the poll loop keeps running after the user
        // navigates away; without this guard the final navigate('/') yanked
        // them out of whatever tab they'd moved to.
        const stillHere = () => window.location.pathname === '/premium/success';
        if (sid) {
          // Poll up to 6× with 1.5s gap so a slow webhook still lands.
          for (let i = 0; i < 6; i++) {
            if (!stillHere()) return;
            try {
              const r = await api.get('/billing/session-status', { params: { session_id: sid } });
              if (r.data?.premium) {
                toast.success('Payment confirmed — you are now ad-free for life!');
                // Funnel step 3 — ad-free conversion (confirmed premium).
                // `revenue` (USD) powers the PostHog revenue tile.
                posthogCapture('ad-free', {
                  source: 'stripe',
                  revenue: typeof r.data.amount_total === 'number' ? r.data.amount_total / 100 : 4.99,
                  currency: r.data.currency || 'usd',
                });
                // Ad-pixel Purchase (TikTok CompletePayment + Meta Purchase).
                // session_id doubles as the dedup event_id, so a success-page
                // reload can't double-count the conversion.
                adTrackPurchase({
                  value: typeof r.data.amount_total === 'number' ? r.data.amount_total / 100 : 4.99,
                  currency: (r.data.currency || 'usd').toUpperCase(),
                  orderId: sid,
                });
                refreshUser && refreshUser();
                break;
              }
            } catch (e) { /* ignore — keep polling */ }
            await new Promise(res => setTimeout(res, 1500));
          }
        }
        if (stillHere()) navigate('/', { replace: true });
      })();
      return;
    }
    if (path === '/premium/cancel') {
      toast.info('Payment cancelled — you can come back anytime.');
      navigate('/', { replace: true });
      return;
    }

    // v5.7.81 — URL → stack mapping. The old single-slot model called
    // `setSubView/setSubViewData` directly; now we REPLACE the entire
    // stack with one entry derived from the URL. This is correct for
    // deep-link entry points (someone opens /person/abc from a chat)
    // because we have no history to unwind, but it also runs on
    // popstate from in-app navigation — in that case `lastHandledPath`
    // guards against re-running here when navigateTo just pushed the
    // same path itself.
    const m = (re) => path.match(re);

    let mPerson, mTeam, mLeague, mDraft;
    if ((mPerson = m(/^\/person\/([^/]+)\/?$/))) {
      setActiveTab('stage');
      setNavStack([{ view: 'entity-detail', data: decodeURIComponent(mPerson[1]) }]);
    } else if ((mTeam = m(/^\/team\/([^/]+)\/?$/))) {
      setActiveTab('stage');
      setNavStack([{ view: 'team-view', data: decodeURIComponent(mTeam[1]) }]);
    } else if ((mLeague = m(/^\/league\/([^/]+)\/?$/))) {
      setActiveTab('rankings');
      setNavStack([]);
      setSelectedLeagueId(decodeURIComponent(mLeague[1]));
    } else if ((mDraft = m(/^\/draft\/([^/]+)\/?$/))) {
      // Draft room reads teamId from currentTeam. If the URL id doesn't
      // match the current team, defer to team-view which accepts any id.
      const draftId = decodeURIComponent(mDraft[1]);
      setActiveTab('stage');
      if (currentTeamIdRef.current === draftId) {
        setNavStack([{ view: 'draft-room', data: null }]);
      } else {
        setNavStack([{ view: 'team-view', data: draftId }]);
      }
    } else if (path === '/' || path === '/stage') {
      // Tab-root path → restore the tab AND drain stack so back-from-deep-link
      // (and a fresh remount after returning from /legal/takedown) lands on the
      // SAME tab the user left, not always the default Stage.
      setActiveTab('stage');
      setNavStack([]);
    } else if (path === '/explore' || path === '/news' || path === '/rankings' || path === '/more') {
      setActiveTab(path.slice(1));
      setNavStack([]);
    }
    // All other paths fall through to default state.
    // We don't replace the URL — back-button still works.
    window.scrollTo(0, 0);
    // 2026-07-20 perf audit — currentTeam?.id read via ref: keeping it in the
    // dep array re-ran this effect on EVERY team refresh (rewarded ads, score
    // ticks) even though the path hadn't changed.
  }, [user, loading, location.pathname, navigate, refreshUser]);
  
  // v5.7.92 — Removed unused `scrollMemory` ref (was a no-op placeholder
  // since v5.7.60 when the fixed-overlay sub-view rewrite started
  // preserving tab scroll natively).
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const subViewRef = useRef(subView);
  subViewRef.current = subView;
  // 2026-07 (iter-137) — mirror of navStack for goBack. Lets goBack compute
  // the next stack OUTSIDE the setState updater: calling pushUrl→navigate()
  // inside the updater ran during React's render phase and triggered
  // "Cannot update a component (BrowserRouter) while rendering MainApp".
  const navStackRef = useRef(navStack);
  navStackRef.current = navStack;

  // Apply saved theme on startup — handled in App() component above

  // Smooth scroll helper — native compositor-optimized scroll (2026-07 UX
  // audit P1: the old rAF loop mutated scrollTop per frame → layout thrash).
  const smoothScrollElementFn = (element) => {
    element.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
    // Always show Dev News after the splash. The user explicitly asked
    // for this in v5.7.39 — they wanted to see "what's new" every cold
    // load, not just once per device. No localStorage gate. The Dev
    // News modal has its own dismiss button, so showing it on every
    // splash-completion is cheap and clear.
    //
    // v6.1.4 P0 — EXCEPT on same-tab reloads where a session was
    // already active. The `fws_session_active` sessionStorage flag is
    // set by AuthContext after a successful bootstrap; if it's
    // present we skip the DevNews replay too. New tabs / fresh
    // launches don't have the flag, so they see DevNews as before.
    let sessionActive = false;
    try { sessionActive = sessionStorage.getItem('fws_session_active') === '1'; }
    catch { /* private mode */ }
    // UX-1: also skip if the user already saw + dismissed DevNews earlier
    // in THIS browser session (`fws_devnews_seen`). Stored in sessionStorage
    // so it reappears on a fresh cold load / new tab, but not on in-app
    // navigation or login→logout→login within the same session.
    let devNewsSeen = false;
    try { devNewsSeen = sessionStorage.getItem('fws_devnews_seen') === '1'; }
    catch { /* private mode */ }
    // v6.1.5 P0 — Never gate automated/E2E sessions behind the DevNews
    // overlay. Playwright (and most headless drivers) expose
    // `navigator.webdriver === true`; real users never do. Skipping the
    // overlay for automation unblocks the testing agent / screenshot tool
    // without changing anything for actual users. Manual testers can also
    // force-skip via the `?e2e=1` query flag.
    let isAutomated = false;
    try {
      isAutomated = navigator.webdriver === true ||
        new URLSearchParams(window.location.search).get('e2e') === '1';
    } catch { /* noop */ }
    if (!sessionActive && !devNewsSeen && !isAutomated) setShowDevNews(true);
  }, []);

  const switchTab = useCallback((tab) => {
    if (tab === activeTabRef.current) {
      // Already on this tab — if we're inside a sub-view, treat the tap as
      // "back to tab root" (matches iOS/Android Material BottomNavigation
      // behaviour where re-tapping the active tab pops the stack).
      if (subViewRef.current) {
        // v5.7.81 — Drain the entire stack on same-tab re-tap. Single
        // pop would only retreat one level; the iOS convention is to
        // pop all the way back to root.
        setNavStack([]);
        const tabPath = tab === 'stage' ? '/' : `/${tab}`;
        pushUrl(tabPath);
        window.scrollTo(0, 0);
        // 2026-08-15 — tabs can listen for re-taps (News resets to World).
        window.dispatchEvent(new CustomEvent('fws:tab-retap', { detail: { tab } }));
        return;
      }
      // No sub-view → smooth scroll to top instead.
      if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        document.querySelectorAll('[data-radix-scroll-area-viewport]').forEach(vp => {
          if (vp.scrollTop > 0) smoothScrollElementFn(vp);
        });
      }, 16);
      window.dispatchEvent(new CustomEvent('fws:tab-retap', { detail: { tab } }));
      return;
    }
    // v5.7.81 — Switching to a DIFFERENT tab drains the stack. Each
    // tab gets its own fresh navigation stack (no cross-tab leakage).
    setNavStack([]);
    setActiveTab(tab);
    window.scrollTo(0, 0);
    // Sync URL — tabs use simple top-level paths
    const tabPath = tab === 'stage' ? '/' : `/${tab}`;
    pushUrl(tabPath);
    // Trigger an ad check on tab-switch for non-premium users (Ad-free is the
    // only premium feature; the AdProvider enforces grace period + cadence).
    triggerAdCheck();
  }, [triggerAdCheck, pushUrl]);

  const navigateTo = useCallback((view, data) => {
    // v5.7.81 — Push onto the stack instead of clobbering. Each new
    // sub-view is layered on top of the previous one; goBack pops one
    // level at a time. The TOP entry is what the user sees.
    setNavStack((s) => [...s, { view, data }]);
    // Sync URL when the view maps to a shareable canonical path.
    // (URL changes also feed the browser back-gesture → popstate path.)
    if (view === 'entity-detail' && data)  pushUrl(`/person/${data}`);
    else if (view === 'team-view' && data) pushUrl(`/team/${data}`);
    else if (view === 'draft-room' && currentTeam?.id) pushUrl(`/draft/${currentTeam.id}`);
  }, [pushUrl, currentTeam?.id]);

  const goBack = useCallback(() => {
    // v5.7.81 — Pop ONE level from the stack. If the resulting stack is
    // empty, the user is back at the tab root. 2026-07 (iter-137) — next
    // stack is computed from navStackRef so the pushUrl→navigate() URL sync
    // happens in the event handler, NOT inside the setState updater (which
    // React runs during render → BrowserRouter setState-in-render warning).
    const next = navStackRef.current.slice(0, -1);
    setNavStack(next);
    const tab = activeTabRef.current;
    if (next.length === 0) {
      pushUrl(tab === 'stage' ? '/' : `/${tab}`);
    } else {
      const top = next[next.length - 1];
      if (top.view === 'entity-detail' && top.data) pushUrl(`/person/${top.data}`);
      else if (top.view === 'team-view' && top.data) pushUrl(`/team/${top.data}`);
      else if (top.view === 'draft-room' && currentTeam?.id) pushUrl(`/draft/${currentTeam.id}`);
      else pushUrl(tab === 'stage' ? '/' : `/${tab}`);
    }
    // Scroll state of the PARENT layer (and the tab root) is preserved
    // for free because we keep all stack entries mounted with display:none
    // on lower layers — see renderContent below.
  }, [pushUrl, currentTeam?.id]);

  // 2026-07 audit P1 — stable sub-view openers. renderTabContent /
  // renderStackFrame previously created fresh arrow-function props on every
  // MainApp render, busting child memoization and re-firing any child effect
  // keyed on them. One useMemo map keyed on the (already-stable) navigateTo.
  const nav = useMemo(() => ({
    entity: (id) => { entityContextTeamRef.current = null; navigateTo('entity-detail', id); },
    teamView: (tid) => navigateTo('team-view', tid),
    draft: () => navigateTo('draft-room'),
    teamMgmt: () => navigateTo('team-mgmt'),
    premium: () => navigateTo('premium'),
    transferMarket: () => navigateTo('transfer-market'),
    formation: () => navigateTo('formation'),
    boosters: () => navigateTo('boosters'),
    watchlist: () => navigateTo('watchlist'),
    leagueActivity: () => navigateTo('league-activity'),
    notifications: () => navigateTo('notifications'),
    community: () => navigateTo('community'),
    scoringRules: () => navigateTo('scoring-rules'),
    profile: (data) => navigateTo('profile', data && data.focus ? data : undefined),
    about: () => navigateTo('about'),
    userStats: () => navigateTo('user-stats'),
    leagueEntitySelect: (leagueId, slotConfig, maxPlayers) =>
      navigateTo('league-entity-select', { leagueId, slotConfig, maxPlayers }),
  }), [navigateTo]);
  const openLeagueChat = useCallback(() => navigateTo('league-chat', {
    leagueId: selectedLeagueId,
    leagueName: selectedLeague?.name || 'League Chat',
  }), [navigateTo, selectedLeagueId, selectedLeague?.name]);

  // v5.7.81 — popstate listener: device/browser back gesture pops one
  // level off the nav stack to match the in-app back arrow. Without
  // this, hardware back would just change the URL while leaving the
  // overlay mounted (visual mismatch). Re-tapping the in-app back arrow
  // is idempotent because it also calls navigate(-1) via pushUrl.
  useEffect(() => {
    const onPopState = () => {
      // If we have stack entries to unwind, pop one AND claim ownership of
      // this navigation (navSourceRef) so the URL→state effect doesn't ALSO
      // re-map the URL into a fresh stack — that double-handling collapsed
      // multi-level stacks to root. With an EMPTY stack we leave the flag
      // unset so the effect handles the route (e.g. a cross-tab back gesture).
      // 2026-07 UX audit P1 — registered ONCE (reads navStackRef) instead of
      // re-registering on every stack-depth change.
      if (navStackRef.current.length > 0) {
        navSourceRef.current = 'popstate';
        setNavStack((s) => s.slice(0, -1));
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // v6.x — Welcome-email CTA deep link. The "Claim your starting budget →
  // draft your team" button links to `…/?start=draft`. Once the freshly
  // onboarded user's team has loaded (and the first-login How-to-Play guide
  // is dismissed), drop them straight into the Draft Room. Graceful: if the
  // team isn't ready yet we just wait — the effect re-runs when it arrives.
  // Fires once, then navigateTo replaces the URL with /draft/:id.
  const welcomeDraftHandled = useRef(false);
  useEffect(() => {
    if (!user || loading || isFirstLogin || welcomeDraftHandled.current) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('start') !== 'draft') return;
    if (!currentTeam?.id) return;            // wait for the team to load
    welcomeDraftHandled.current = true;
    setActiveTab('stage');
    navigateTo('draft-room');                // pushes /draft/:id + opens room
  }, [user, loading, isFirstLogin, currentTeam?.id, navigateTo]);

  // v6.13 — Trending-digest email CTA (`…/?start=explore`) lands the user on
  // the Explore tab (the "Trending Now" strip). Fires once after auth loads.
  const exploreDeepLinkHandled = useRef(false);
  useEffect(() => {
    if (!user || loading || exploreDeepLinkHandled.current) return;
    if (new URLSearchParams(window.location.search).get('start') !== 'explore') return;
    exploreDeepLinkHandled.current = true;
    setNavStack([]);
    setActiveTab('explore');
  }, [user, loading]);

  // Build 4 — never replay Developer News over a SIGNED-IN session: once
  // auth resolves with a user, drop the pending flag entirely. Dev News is
  // an anonymous/logged-out splash follow-up only.
  useEffect(() => {
    if (showDevNews && !loading && user) setShowDevNews(false);
  }, [showDevNews, loading, user]);

  // v5.7.60 — Lock body scroll while a sub-view overlay is open so the
  // underlying tab doesn't accidentally scroll behind the modal-style
  // overlay (especially on mobile where touch can leak through).
  useEffect(() => {
    if (subView) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [subView]);

  if (showSplash) return <SplashScreen onComplete={handleSplashComplete} />;
  if (showDevNews && !loading && !user) return <DeveloperNewsScreen onClose={() => { try { sessionStorage.setItem('fws_devnews_seen', '1'); } catch { /* private mode */ } setShowDevNews(false); setActiveTab('stage'); setNavStack([]); window.scrollTo(0, 0); }} />;
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Skeleton className="h-32 w-64 rounded-2xl" /></div>;
  if (!user) return <AuthPage />;
  
  // First-time user: Build 4 — 3-screen visual onboarding carousel.
  if (isFirstLogin) {
    return <HowToPlayOnboarding
      onClose={() => {
        clearFirstLogin();
        setActiveTab('stage');
        setNavStack([]);
        window.scrollTo(0, 0);
      }}
      onStartDrafting={() => {
        clearFirstLogin();
        setActiveTab('stage');
        setNavStack([]);
        window.scrollTo(0, 0);
        if (currentTeam?.id) navigateTo('draft-room');
      }}
    />;
  }

  const teamId = currentTeam?.id;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'news': return <NewsTab selectedLeagueId={selectedLeagueId} currentTeam={currentTeam} onEntityClick={nav.entity} />;
      case 'rankings': return <RankingsTab selectedLeagueId={selectedLeagueId} onTeamClick={nav.teamView} onFinishTeam={() => switchTab('stage')} />;
      case 'stage':
        return (
          <div>
            <OnboardingAttachBanner
              status={attachStatus.status}
              attached={attachStatus.attached}
              total={attachStatus.total}
              onRetry={attachStatus.retry}
            />
            {!currentTeam?.frozen && (
            <OnboardingBanner
              onDraft={nav.draft}
              onManageTeam={nav.teamMgmt}
              // 2026-09-07 — StageTab owns the confirm dialog + overlay; the banner just asks.
              onDraftRandom={() => window.dispatchEvent(new CustomEvent('fws:draft-random'))}
              onFinalize={() => window.dispatchEvent(new CustomEvent('fws:finalize-request'))}
              currentTeam={currentTeam}
              leagueSettings={selectedLeague?.settings}
            />
            )}
            <StageTab
              selectedLeagueId={selectedLeagueId}
              selectedLeagueName={selectedLeague?.name || ''}
              currentTeam={currentTeam}
              onTeamUpdate={setCurrentTeam}
              leagueSettings={selectedLeague?.settings}
              onManageTeam={nav.teamMgmt}
              // 2026-09-17 — "We couldn't reach your email" card → profile email fix
              onFixEmail={() => nav.profile({ focus: 'email' })}
              onDraft={nav.draft}
              onEntityClick={nav.entity}
              onTeamClick={nav.teamView}
              onTransferMarket={nav.transferMarket}
              onFormation={nav.formation}
              onBoosters={nav.boosters}
              onWatchlist={nav.watchlist}
              onLeagueActivity={nav.leagueActivity}
              // v5.7.89 — per-league chat (button between Rankings + Gameweek timeline)
              onLeagueChat={openLeagueChat}
            />
          </div>
        );
      case 'explore':
        return <ExploreTab onEntityClick={nav.entity} onTeamUpdate={setCurrentTeam} currentTeam={currentTeam} leagueSettings={selectedLeague?.settings} selectedLeagueId={selectedLeagueId} />;
      case 'more':
        return (
          <MoreTab
            selectedLeagueId={selectedLeagueId}
            onNotifications={nav.notifications}
            onCommunity={nav.community}
            onPremium={nav.premium}
            onScoringRules={nav.scoringRules}
            onProfile={nav.profile}
            onAbout={nav.about}
            onUserStats={nav.userStats}
            onWatchlist={nav.watchlist}
            onLeagueEntitySelect={nav.leagueEntitySelect}
          />
        );
      default: return <StageTab />;
    }
  };

  // v5.7.81 — Render a SINGLE stack frame. Used by `renderContent` to
  // mount EVERY entry in the navStack (lower ones hidden via display:none
  // so their state persists). Splitting this out lets each frame
  // re-render independently without re-mounting the whole stack.
  const renderStackFrame = (frame) => {
    const v = frame.view;
    const d = frame.data;
    // Sub-views — all get currentTeam where relevant
    if (v === 'entity-detail' && d) {
      // 2026-08-12 (owner request) — contextTeam (an opponent's team, set via
      // entityContextTeamRef just before navigation) makes the Purchase Price
      // card show what THAT manager paid ("Bought For"). `d` stays a bare id
      // so the /person/:id URL routing is unaffected.
      return <EntityDetail entityId={d} onBack={goBack} teamData={currentTeam} contextTeam={entityContextTeamRef.current} selectedLeague={selectedLeague} onUpgrade={() => navigateTo('premium')} />;
    }
    if (v === 'team-view' && d)      return <TeamViewPage teamId={d} onBack={goBack} onEntityClick={(id, viewedTeam) => { entityContextTeamRef.current = (viewedTeam && !viewedTeam.is_owner) ? viewedTeam : null; navigateTo('entity-detail', id); }} />;
    if (v === 'draft-room')          return <DraftRoom onBack={goBack} teamId={teamId || currentTeam?.id} leagueId={selectedLeagueId} poolSize={selectedLeague?.pool_size || 0} onTeamUpdate={setCurrentTeam} leagueSettings={selectedLeague?.settings} leagueName={selectedLeague?.name || 'Global League'} onEntityClick={(id) => navigateTo('entity-detail', id)} />;
    if (v === 'team-mgmt')           return <TeamManagement onBack={goBack} teamId={teamId || currentTeam?.id} leagueId={selectedLeagueId} onTeamUpdate={setCurrentTeam} onEntityDetail={(id) => navigateTo('entity-detail', id)} leagueName={selectedLeague?.name} leagueSettings={selectedLeague?.settings} />;
    if (v === 'formation')           return <FormationView onBack={goBack} teamId={teamId || currentTeam?.id} onEntityClick={(id) => navigateTo('entity-detail', id)} onDraft={() => navigateTo('draft-room')} />;
    if (v === 'premium')             return <PremiumPage onBack={goBack} />;
    if (v === 'boosters')            return <BoostersPage teamId={teamId} onBack={goBack} />;
    if (v === 'notifications')       return <NotificationsPanel onBack={goBack} team={currentTeam} />;
    if (v === 'community')           return <CommunityChat leagueName="Global Chat" onBack={goBack} />;
    // v5.7.89 — Per-league chat. The user opens this from the Stage tab
    // via the "[League Name] Chat" button between Mini Rankings and the
    // Gameweek timeline. `data` is { leagueId, leagueName }. Re-uses the
    // same CommunityChat component as the global chat — the backend
    // membership check (community.py) enforces privacy.
    if (v === 'league-chat' && d)    return <CommunityChat leagueId={d.leagueId} leagueName={d.leagueName} onBack={goBack} />;
    if (v === 'scoring-rules')       return <HowToPlayAndRules onBack={goBack} />;
    if (v === 'profile')             return <ProfilePage onBack={goBack} focusEmail={d?.focus === 'email'} />;
    if (v === 'about')               return <AboutPage onBack={goBack} />;
    if (v === 'league-entity-select' && d) return <LeagueEntitySelector leagueId={d.leagueId} slotConfig={d.slotConfig} maxPlayers={d.maxPlayers} onDone={goBack} onBack={goBack} />;
    if (v === 'watchlist')           return <WatchlistPage onEntityClick={nav.entity} onBack={goBack} />;
    if (v === 'league-activity')     return <LeagueActivityPage selectedLeagueId={selectedLeagueId} leagueName={selectedLeague?.name || ''} onTeamClick={nav.teamView} onEntityClick={nav.entity} onBack={goBack} />;
    if (v === 'transfer-market')     return <TransferMarket onEntityClick={nav.entity} onBack={goBack} team={currentTeam} selectedLeagueId={selectedLeagueId} />;
    if (v === 'user-stats')          return <UserStatsPage onBack={goBack} />;
    return null;
  };

  // v5.7.81 — Render the tab AND every stack frame together. Each frame
  // is a `position: fixed` overlay; the TOP frame is visible, lower
  // frames stay mounted (`display:none`) so their scroll position,
  // search filters, etc. survive back-navigation natively. This is the
  // generalized v5.7.60 fixed-overlay pattern from one-deep to N-deep.
  const renderContent = () => {
    const tabContent = renderTabContent();
    const hasStack = navStack.length > 0;
    const topIdx = navStack.length - 1;
    return (
      <>
        {/* 2026-07 UX audit P1 — the tab root stays mounted (state/scroll
            preserved) but is visibility-hidden while an overlay covers it,
            so the browser skips paint/raster work for the obscured tree.
            (display:none would collapse layout and reset scroll positions.)
            The wrapper renders unconditionally so the tree position is
            stable — no remount when the first overlay opens/closes. */}
        <div style={hasStack ? { visibility: 'hidden' } : undefined} aria-hidden={hasStack ? 'true' : undefined}>
          {tabContent}
        </div>
        {navStack.map((frame, idx) => {
          const isTop = idx === topIdx;
          // Stable key so React doesn't re-mount frames on push/pop —
          // frame's view+data is unique enough; stack-index is the
          // tiebreaker when the same view appears twice (e.g.
          // entity-detail → entity-detail through a "related people"
          // chain).
          const key = `${idx}-${frame.view}-${typeof frame.data === 'string' ? frame.data : 'obj'}`;
          return (
            <div
              key={key}
              style={{
                position: 'fixed',
                inset: 0,
                // Top frame on a higher z to capture interaction; lower
                // frames are HIDDEN (display:none, not visibility:hidden
                // — visibility:hidden still consumes pointer events on
                // some browsers).
                zIndex: 40 + idx,
                background: 'hsl(var(--background))',
                overflowY: 'auto',
                paddingBottom: '64px',
                display: isTop ? 'block' : 'none',
              }}
              data-subview-overlay
              data-stack-depth={idx}
            >
              {renderStackFrame(frame)}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="app-container" data-testid="main-app">
      <a
        href="#main-content"
        className="skip-link"
        data-testid="skip-to-content"
        onClick={(e) => {
          e.preventDefault();
          const m = document.getElementById('main-content');
          if (m) { m.focus(); m.scrollIntoView({ block: 'start' }); }
        }}
      >
        Skip to main content
      </a>
      {activeTab !== 'more' && !subView && (
        <GlobalHeader
          selectedLeagueId={selectedLeagueId}
          onLeagueChange={(leagueId, team, league) => {
            const isLeagueSwitch = leagueId !== selectedLeagueId;
            setSelectedLeagueId(leagueId);
            // 2026-06 (iter_187 P0) — a real league switch adopts whatever the
            // header resolved (null = "no team in that league" is legitimate).
            // A SAME-league call is a header remount (back from Draft Room):
            // recency-guard it so a stale/null snapshot can't clobber the
            // fresh post-draft team.
            if (isLeagueSwitch) setCurrentTeam(team);
            else adoptTeamSnapshot(team);
            // 2026-08-15 (testing-agent P0) — GlobalHeader passes the
            // LIGHTWEIGHT list object (no `settings`); a header remount
            // (returning from Draft Room etc.) must never clobber the
            // detail-fetched league carrying slot_config. Merge over the
            // previous/cached detail object instead of replacing.
            setSelectedLeague((prev) => {
              const base = (prev && prev.id === leagueId) ? prev : leagueCacheRef.current.get(leagueId);
              return base ? { ...base, ...(league || {}) } : (league || null);
            });
          }}
        />
      )}
      <main id="main-content" tabIndex={-1} className="page-content focus:outline-none">{renderContent()}</main>
      <BottomTabBar activeTab={activeTab} onTabChange={switchTab} />
      {/* 2026-08-27 — one-time "save your team" prompt after a guest's
          first draft (funnel: only 5% of guests finalize). */}
      <GuestFirstDraftPrompt />
      {/* 2026-08-28 — "finalize now" prompt the moment a draft completes
          the roster (top funnel drop-off). */}
      <FinalizeNowPrompt onGoToStage={() => {
        try { sessionStorage.setItem('fws_pending_finalize', '1'); } catch (e) { /* private mode */ }
        switchTab('stage');
        window.dispatchEvent(new CustomEvent('fws:finalize-request'));
      }} />
      {/* 2026-11-01 — one-time quarter recap when a Global Quarterly closes. */}
      <QuarterRecapPrompt
        onViewStandings={(leagueId) => {
          setSelectedLeagueId(leagueId);
          window.dispatchEvent(new CustomEvent('fws:select-league', { detail: { leagueId } }));
          switchTab('rankings');
        }}
      />
      <InstallAppBanner path={
        // v5.8.21 — Show the install banner on BOTH deep-link paths
        // (which get the deferred-bounce-back treatment) AND on tab
        // roots (which get the general "Get App" treatment with a 24h
        // dismissal cooldown so we don't pester users every session).
        /^\/(person|team|league|draft)\/[A-Za-z0-9._-]{1,80}$/.test(location.pathname)
          ? location.pathname
          : null
      } />
      <AdPopup
        onAction={(action) => {
          if (action === 'premium') navigateTo('premium');
          else if (action === 'explore') { setNavStack([]); setActiveTab('explore'); }
          else if (action === 'create-league') { setNavStack([]); setActiveTab('more'); }
        }}
      />
    </div>
  );
}

function RequireAdmin({ children }) {
  // AUDIT P2 (2026-06) — client-side admin guard. The server already enforces
  // 401/403 on every admin API, but without this a non-admin could lazy-load
  // the heavy admin bundle and probe endpoints. While auth is resolving we hold
  // a skeleton (never flash the admin UI); non-admins are redirected home so the
  // lazy admin chunk never even mounts/imports.
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="admin-guard-loading">
        <Skeleton className="h-10 w-40" />
      </div>
    );
  }
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppContent() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="route-lazy-fallback">
        <Skeleton className="h-10 w-40" />
      </div>
    }>
    <Routes>
      <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
      {/* v6.1.17.7 N.2-OAuth — OAuth callback (Google + Apple) */}
      <Route path="/auth/oauth/:provider/callback" element={<OAuthCallback />} />
      <Route path="/admin/portraits" element={<RequireAdmin><AdminPortraitsPage /></RequireAdmin>} />
      {/* v6.1.17.1 Phase L.x.1 — Unified audit dashboard.  The legacy
          /admin/security page (`AdminSecurityPage`) is superseded by the
          K dashboard at /admin/audits which shows BOTH the new K runs
          AND the migrated historical admin_jobs runs (114+ rows
          unified).  The legacy import is preserved so deep links from
          old bookmarks / external systems still resolve, but the route
          server-redirects with `replace` so the back-button doesn't
          loop.  Banner inside AuditDashboard explains the move (post-
          deploy 7-day window via localStorage flag). */}
      <Route path="/admin/security" element={<Navigate to="/admin/audits" replace />} />
      <Route path="/admin/audits" element={<RequireAdmin><AuditDashboard /></RequireAdmin>} />
      <Route path="/admin/chat" element={<RequireAdmin><AdminChatModerationPage /></RequireAdmin>} />
      <Route path="/people" element={<PeopleBrowsePage />} />
      <Route path="/legal/takedown" element={<LegalTakedownPage />} />
      <Route path="/legal/inclusion" element={<LegalInclusionPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      {/* v6.x — Password-reset link target (emailed). Public route —
          the user is logged out while resetting. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/credits" element={<CreditsPage />} />
      <Route path="*" element={<MainApp />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  // Apply saved theme on mount, and keep the system-preference listener
  // attached for the lifetime of the app so the Auto mode always re-reads
  // the current OS setting (the previous version only listened while the
  // saved theme was already "auto" at mount, so toggling Light → Dark → Auto
  // never re-checked the OS).
  useEffect(() => {
    const apply = () => {
      const savedTheme = localStorage.getItem('theme') || 'auto';
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else if (savedTheme === 'light') {
        document.documentElement.classList.remove('dark');
      } else {
        // Auto — track the device setting at this very moment.
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.classList.toggle('dark', prefersDark);
      }
    };

    apply();
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => apply();           // re-evaluate on every OS change
    mql.addEventListener('change', handler);
    // Also re-evaluate when MoreTab writes a new value to localStorage
    // (so the App-level listener and the in-page theme picker stay in sync).
    window.addEventListener('theme:changed', apply);
    return () => {
      mql.removeEventListener('change', handler);
      window.removeEventListener('theme:changed', apply);
    };
  }, []);

  return (
    <ErrorBoundary>
      {/* v6.8.0 — Site access gate wraps EVERYTHING (including the auth
          context). It's BACKEND-enforced now: PreLaunchGate asks
          GET /api/gate/status and only renders children when the gate is
          disabled or already unlocked. Controlled by backend env vars
          (SITE_GATE_ENABLED + SITE_GATE_CODE) — the secret never ships in
          the JS bundle, and the API is blocked server-side by
          SiteGateMiddleware until unlocked. Off by default. Layer Cloudflare
          Zero Trust on top of this for defense-in-depth. */}
      <PreLaunchGate>
        <AuthProvider>
          <AdProvider>
            <BrowserRouter><AdPixelsTracker /><AppContent /></BrowserRouter>
          </AdProvider>
          <Toaster position="top-center" richColors />
          <CookieBanner />
          {/* v6.1.17.7 N.2 — Global username gate UI.  Listens on
              the `fws:username-required` window event fired by the
              axios 403 interceptor in `src/lib/api.js`.  Shows the
              no-dismiss picker until the user sets a username. */}
          <UsernamePickerHost />
          {/* 18+ gate (2026-06) — listens on the `fws:age-required` window
              event fired by the axios 403 interceptor and shows a no-dismiss
              18+ confirmation that POSTs /api/auth/confirm-age then reloads. */}
          <AgeGateHost />
        </AuthProvider>
      </PreLaunchGate>
    </ErrorBoundary>
  );
}

export default App;
